import React, { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import ProtectedRoute from './ProtectedRoute'
import AdminRoute from './AdminRoute'
import MainLayout from '../layouts/MainLayout'
import AuthLayout from '../layouts/AuthLayout'
import AdminLayout from '../layouts/AdminLayout'

// Lazy load pages for better performance
const HomePage = lazy(() => import('../pages/home/HomePage.jsx'))
const Login = lazy(() => import('../pages/auth/Login'))
const Register = lazy(() => import('../pages/auth/Register'))
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'))
const VerifyEmail = lazy(() => import('../pages/auth/VerifyEmail'))
const ProductList = lazy(() => import('../pages/products/ProductList'))
const ProductDetail = lazy(() => import('../pages/products/ProductDetail'))
const ProductSearch = lazy(() => import('../pages/products/ProductSearch'))
const CartPage = lazy(() => import('../pages/cart/CartPage'))
const CheckoutPage = lazy(() => import('../pages/checkout/CheckoutPage'))
const OrderConfirmation = lazy(() => import('../pages/checkout/OrderConfirmation'))
const OrdersPage = lazy(() => import('../pages/orders/OrdersPage'))
const OrderDetail = lazy(() => import('../pages/orders/OrderDetail'))
const TrackOrder = lazy(() => import('../pages/orders/TrackOrder'))
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'))
const AddressBook = lazy(() => import('../pages/profile/AddressBook'))
const ChangePassword = lazy(() => import('../pages/profile/ChangePassword'))
const CategoriesPage = lazy(() => import('../pages/categories/CategoriesPage'))
const CategoryPage = lazy(() => import('../pages/categories/CategoryPage'))
const AboutPage = lazy(() => import('../pages/about/AboutPage'))
const ContactPage = lazy(() => import('../pages/contact/ContactPage'))
const FAQPage = lazy(() => import('../pages/faq/FAQPage'))
const MyMessagesPage = lazy(() => import('../pages/contact/MyMessagesPage.jsx'))
const PaymentSuccess = lazy(() => import('../pages/payment/PaymentSuccess'))
const PaymentFailed = lazy(() => import('../pages/payment/PaymentFailed'))

// Policy Pages
const ShippingPolicy = lazy(() => import('../pages/policies/ShippingPolicy'))
const ReturnPolicy = lazy(() => import('../pages/policies/ReturnPolicy'))
const PrivacyPolicy = lazy(() => import('../pages/policies/PrivacyPolicy'))
const TermsConditions = lazy(() => import('../pages/policies/TermsConditions'))
const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsPage'))

// Admin pages
const AdminDashboard = lazy(() => import('../pages/admin/Dashboard/AdminDashboard.jsx'))
const AdminProducts = lazy(() => import('../pages/admin/products/AdminProducts.jsx'))
const ProductForm = lazy(() => import('../pages/admin/products/ProductForm.jsx'))
const AdminOrders = lazy(() => import('../pages/admin/Orders/AdminOrders'))
const AdminOrderDetails = lazy(() => import('../pages/admin/Orders/OrderDetails'))
const AdminUsers = lazy(() => import('../pages/admin/Users/AdminUsers'))
const AdminCategories = lazy(() => import('../pages/admin/Categories/AdminCategories'))
const AdminCoupons = lazy(() => import('../pages/admin/Coupons/AdminCoupons'))
const AdminReports = lazy(() => import('../pages/admin/reports/AdminReports'))
const AdminHomepageSections = lazy(() => import('../pages/admin/HomePage/AdminHomepageSections.jsx'))
const AdminFlashExpressSettings = lazy(() => import('../pages/admin/Shipping/AdminFlashExpressSettings.jsx'))
const AdminShippingPanel = lazy(() => import('../pages/admin/Shipping/AdminShippingPanel.jsx'))
const AdminJntShipping = lazy(() => import('../pages/admin/Shipping/AdminJntShipping.jsx'))
const AdminSettings = lazy(() => import('../pages/admin/settings/AdminSettings.jsx'))
const AdminNewsletter = lazy(() => import('../pages/admin/Newsletter/AdminNewsletter.jsx'))
const AdminNotifications = lazy(() => import('../pages/admin/Notifications/AdminNotifications'))
const AdminContactMessages = lazy(() => import('../pages/admin/contact/AdminContactMessages.jsx'))

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center px-4">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-6">Page Not Found</p>
      <a href="/" className="inline-block px-6 py-3 bg-[#ff6900] text-white rounded-lg hover:bg-[#ff6900] transition-colors">
        Go Home
      </a>
    </div>
  </div>
)

const AppRoutes = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Public Routes - No authentication required */}
        <Route path="/" element={<MainLayout />}>
          {/* Home & General Pages */}
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />

          {/* Product Routes */}
          <Route path="products" element={<ProductList />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="search" element={<ProductSearch />} />

          {/* Category Routes */}
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="categories/:id" element={<CategoryPage />} />

          {/* Cart & Checkout */}
          <Route path="cart" element={<CartPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="order-confirmation/:id" element={<OrderConfirmation />} />

          {/* Payment Routes - Accessible by both guests and logged-in users */}
          <Route path="payment/success" element={<PaymentSuccess />} />
          <Route path="payment/failed" element={<PaymentFailed />} />
          <Route path="payment/cancelled" element={<PaymentFailed />} />

          {/* Policy Pages */}
          <Route path="shipping-policy" element={<ShippingPolicy />} />
          <Route path="return-policy" element={<ReturnPolicy />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-conditions" element={<TermsConditions />} />
        </Route>

        {/* Auth Routes - No header/footer, centered forms */}
        <Route path="/" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="verify-email" element={<VerifyEmail />} />
        </Route>

        {/* Protected Customer Routes - Requires authentication */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {/* Order Routes */}
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="track-order/:orderNumber" element={<TrackOrder />} />

            {/* Profile Routes */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="addresses" element={<AddressBook />} />
            <Route path="change-password" element={<ChangePassword />} />

            {/* Communication Routes */}
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="my-messages" element={<MyMessagesPage />} />
          </Route>
        </Route>

        {/* Admin Routes - Requires ADMIN role */}
        <Route path="/admin" element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            {/* Dashboard */}
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />

            {/* Product Management */}
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/edit/:id" element={<ProductForm />} />

            {/* Order Management */}
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:id" element={<AdminOrderDetails />} />

            {/* User Management */}
            <Route path="users" element={<AdminUsers />} />

            {/* Category Management */}
            <Route path="categories" element={<AdminCategories />} />

            {/* Coupon Management */}
            <Route path="coupons" element={<AdminCoupons />} />

            {/* Reports */}
            <Route path="reports" element={<AdminReports />} />

            {/* Shipping Management */}
            <Route path="shipping" element={<AdminShippingPanel />} />
            <Route path="shipping/flash-express" element={<AdminFlashExpressSettings />} />

            <Route path="shipping/jnt" element={<AdminJntShipping />} />

            {/* Homepage Management */}
            <Route path="homepage" element={<AdminHomepageSections />} />

            {/* Settings */}
            <Route path="settings" element={<AdminSettings />} />

            {/* Newsletter Management */}
            <Route path="newsletter" element={<AdminNewsletter />} />

            {/* Notifications */}
            <Route path="notifications" element={<AdminNotifications />} />

            {/* Contact Messages */}
            <Route path="contact" element={<AdminContactMessages />} />
          </Route>
        </Route>

        {/* 404 Not Found Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default AppRoutes